import { Spin } from 'antd'
import {
  LABEL_WIDTH,
  SectionContainer,
  Sub,
  TooltipDivider,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { computeMtCylindrical } from 'features/indicationTriage/cards/ffsCalc/foliasProfileLookup'
import isNil from 'lodash/isNil'
import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import {
  computeRt,
  CurveDataProps,
  FfsAcceptabilityCriteriaCurve,
} from './FfsAcceptabilityCriteriaCurve'
import {
  FlawSizeCriteriaCheckResult,
  LABEL_R_T,
} from './FfsFlawSizeCriteriaSection'

const LABEL_FOLIAS_FACTOR = (
  <>
    M<Sub>t</Sub>
  </>
)

export const LABEL_RSFA = (
  <>
    RSF<Sub>a</Sub>
  </>
)

const TOOLTIP_RSF = (
  <>
    Remaining Strength Factor
    <TooltipDivider />
    RSF = {LABEL_R_T} / (1 - (1/{LABEL_FOLIAS_FACTOR}) × (1 - {LABEL_R_T}))
  </>
)

export interface FfsFinalAcceptabilityCriteraSectionValues {
  foliasFactor: GeckoMeasurement | null
  rsfa: GeckoMeasurement | null
  setRsfa: Dispatch<SetStateAction<GeckoMeasurement | null>>
  rsf: GeckoMeasurement | null
  rt: GeckoMeasurement | null
  rtThreshold: GeckoMeasurement | null
}

interface HookProps {
  lambda: GeckoMeasurement | null
  flawSizeCriteriaCheckValues:
    | FlawSizeCriteriaCheckResult['criteriaValues']
    | null
}

export function useLevelOneFinalAcceptabilityCriteriaValues({
  lambda,
  flawSizeCriteriaCheckValues,
}: HookProps): FfsFinalAcceptabilityCriteraSectionValues {
  const [rsfa, setRsfa] = useState<GeckoMeasurement | null>(
    GeckoMeasurement.CreateFromValue(0.9, GeckoMeasurementUnit.Ratio)
  )
  const foliasFactor = useMemo(
    () =>
      lambda
        ? GeckoMeasurement.CreateFromValue(
            computeMtCylindrical(lambda.num),
            GeckoMeasurementUnit.Ratio
          )
        : null,
    [lambda]
  )

  const rt = flawSizeCriteriaCheckValues?.remainingThicknessRatio ?? null
  const rsf = useMemo<GeckoMeasurement | null>(() => {
    const mt = foliasFactor
    if (isNil(rt) || isNil(mt)) {
      return null
    }
    return GeckoMeasurement.CreateFromValue(
      rt.num / (1 - (1 / mt.num) * (1 - rt.num)),
      GeckoMeasurementUnit.Ratio
    )
  }, [foliasFactor, rt])

  const rtThreshold = useMemo<GeckoMeasurement | null>(() => {
    if (isNil(lambda) || isNil(rsfa) || isNil(foliasFactor)) {
      return null
    }
    return GeckoMeasurement.CreateFromValue(
      computeRt(lambda.num, rsfa.num, foliasFactor.num),
      GeckoMeasurementUnit.Ratio
    )
  }, [lambda, rsfa, foliasFactor])

  return useMemo(
    () => ({
      foliasFactor,
      rsfa,
      setRsfa,
      rsf,
      rt,
      rtThreshold,
    }),
    [foliasFactor, rsf, rsfa, rt, rtThreshold]
  )
}

type FfsFinalAcceptabilityCriteraSectionProps =
  FfsFinalAcceptabilityCriteraSectionValues & {
    lambda: GeckoMeasurement | null
    loading: boolean
    curveDataProps: CurveDataProps
  }

const RawFfsFinalAcceptabilityCriteraSection: FC<
  FfsFinalAcceptabilityCriteraSectionProps
> = ({
  foliasFactor,
  loading,
  rsfa,
  setRsfa,
  lambda,
  rsf,
  curveDataProps,
  rt,
}) => {
  return (
    <Spin spinning={loading}>
      <SectionContainer>
        <FfsParameterField
          label={LABEL_RSFA}
          labelTooltip='Acceptable Remaining Strength Factor'
          labelWidth={LABEL_WIDTH}
          value={rsfa}
          setValue={setRsfa}
          measurementType='ratio'
          min={GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Ratio)}
          max={GeckoMeasurement.CreateFromValue(1, GeckoMeasurementUnit.Ratio)}
          step={0.05}
        />
        <FfsParameterField
          label={LABEL_FOLIAS_FACTOR}
          labelTooltip='Folias Factor'
          labelWidth={LABEL_WIDTH}
          value={foliasFactor}
          measurementType='ratio'
        />
        <FfsParameterField
          label='RSF'
          labelTooltip={TOOLTIP_RSF}
          labelWidth={LABEL_WIDTH}
          value={rsf}
          measurementType='ratio'
        />
        {!isNil(lambda) &&
          !isNil(foliasFactor) &&
          !isNil(curveDataProps.curveData) &&
          !isNil(rsf) && (
            <FfsAcceptabilityCriteriaCurve
              {...curveDataProps}
              lambda={lambda.num}
              rsf={rsf.num}
              rt={rt?.num ?? null}
            />
          )}
      </SectionContainer>
    </Spin>
  )
}

export const FfsFinalAcceptabilityCriteraSection = React.memo(
  RawFfsFinalAcceptabilityCriteraSection
)
